// src/utils/AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { url } from "../Constant/TextConstant.js";
import { apiFetch } from "./apiClient.js";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies(["auth"]);
  const [authState, setAuthState] = useState({
    currentUser: null,
    userRole: null,
    currentUserName: null,
    uniqueId: null,
    gender: null,
    referralCode: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const loadAuthData = () => {
      const authData = cookies.auth;
      if (authData) {
        setAuthState({
          ...authData,
          isAuthenticated: true,
          isLoading: false,
        });
      } else {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
      }
    };
    loadAuthData();
  }, [cookies.auth]);

  const login = async (username, password, fcmToken = "") => {
    try {
      const response = await apiFetch(`${url.baseAPIUrl}/api/LoginMaster/Login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          UserName: username,
          Password: password,
          Uniqueid: fcmToken,
          IsWeb: false, 
        }),
      });

      if (!response.ok) {
        throw new Error("Login failed. Please try again.");
      }

      const data = await response.json();

      const authData = {
        currentUser: data.UserName,
        userRole: data.UserRole || "Customer",
        currentUserName: data.Name,
        uniqueId: data.UniqueId,
        gender: data.Gender,
        referralCode: data.ReferralCode,
        isAuthenticated: true,
      };

      setCookie("auth", authData, {
        path: "/",
        maxAge: 604800, // 7 days in seconds
        secure: process.env.NODE_ENV === "production",
        sameSite: "strict",
      });

      setAuthState({
        ...authData,
        isLoading: false,
      });

      toast.success("Login successful!");
      return { success: true, data: authData };
    } catch (error) {
      toast.error(error.message || "Server is currently down");
      return { success: false, error: error.message };
    }
  };

  const logout = async (mobile, uniqueId) => {
    try {
      await apiFetch(`${url.baseAPIUrl}/api/LoginMaster/Logout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          Mobile: mobile,
          UniqueId: uniqueId,
        }),
      });

      removeCookie("auth", { path: "/" });
      setAuthState({
        currentUser: null,
        userRole: null,
        currentUserName: null,
        uniqueId: null,
        gender: null,
        referralCode: null,
        isAuthenticated: false,
        isLoading: false,
      });

      toast.success("Logged out successfully");
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const signup = async (name, mobile, password, gender) => {
    try {
      const response = await apiFetch(
        `${url.baseAPIUrl}/api/LoginMaster/CustomerSignup`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            Name: name,
            Mobile: mobile,
            Password: password,
            Gender: gender,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      toast.success("Signup successful! Please login.");
      return { success: true };
    } catch (error) {
      toast.error(error.message || "Signup failed. Please try again.");
      return { success: false, error: error.message };
    }
  };

  const getCurrentUser = () => authState.currentUser;

  return (
    <AuthContext.Provider
      value={{
        ...authState,
        login,
        logout,
        signup,
        getCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
